import * as React from 'react'
import { DisplayContent } from 'react-content-engine'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'

import { Giftcard } from '../../data/models/giftcard'
import { SiteCurrency } from '../../data/models/locale'
import {
  hideMember,
  IMemberGetMemberState,
} from '../../data/models/member_get_member'
import { AppState } from '../../data/state'

import { Button, TYPE } from 'components/Button'
import FormMessage from 'components/FormMessage'
import { FormMessageType } from 'components/FormMessage/FormMessage'
import Footer from 'components/FunnelFooter'
import Icon from 'components/Icon'
import Overlay from 'components/Overlay'
import { UI } from 'Icon/ui'

import { Slink } from '../../routing/links'
import Summary from '../summary'

interface FunnelFooterOwnProps {
  nextStepNo?: number
  nextStepName: string | JSX.Element
  nextStepLink: string
  nextStepLinkParams?: any
  nextStepDisabled?: boolean
  customPaymentMode?: boolean
  hideGiftcardButton?: boolean
  mobilePositionBottom?: boolean
  stickyStop?: boolean

  nextStepOnClick?(): void
}

interface FunnelFooterProps extends FunnelFooterOwnProps {
  currency: SiteCurrency
  total: number
  oldTotal: number | null
  giftCards: Giftcard[]
  viewSummary: boolean
  member: IMemberGetMemberState
  hideStep?: boolean
  openGiftcardModal: () => void
  setSummary: (val: boolean) => void
  hideTrigger: () => void
  hideTotalWhenDisabled?: boolean
  footerButtonsLayoutClassName?: string
}

interface FunnelFooterState {
  reachedStop: boolean
}

const NextStepButton: React.FC<FunnelFooterProps> = props => {
  const { nextStepNo, nextStepName, nextStepDisabled, nextStepOnClick } = props
  return (
    <Button
      type={TYPE.plain}
      cutOff="none"
      disabled={nextStepDisabled}
      onClick={nextStepOnClick}
    >
      {nextStepNo ? (
        <>
          <span className={props.hideStep ? 'hide-step' : ''}>
            <DisplayContent edit={true} contentKey="step" addNode={false}>
              Step
            </DisplayContent>
            &nbsp;{nextStepNo}
          </span>
          <span className="hidden-mobile"> {nextStepName}</span>
        </>
      ) : (
        <span className="to-payment">{nextStepName}</span>
      )}
      <Icon icon={UI.chevronRight} />
    </Button>
  )
}

class Funnel extends React.Component<FunnelFooterProps, FunnelFooterState> {
  constructor(props: FunnelFooterProps) {
    super(props)
    this.state = {
      reachedStop: false,
    }
  }

  public componentDidMount() {
    if (this.props.stickyStop) {
      this.handler()
      document.addEventListener('scroll', this.handler, false)
      document.addEventListener('resize', this.handler, false)
    }
  }

  public componentWillUnmount() {
    document.removeEventListener('scroll', this.handler)
    document.removeEventListener('resize', this.handler)
  }

  public render() {
    const {
      nextStepDisabled,
      nextStepLink,
      nextStepLinkParams,
      hideGiftcardButton,
      nextStepOnClick,
      hideTotalWhenDisabled,
      total,
      oldTotal,
    } = this.props

    const passedProps = {
      total: hideTotalWhenDisabled && nextStepDisabled ? NaN : total,
      oldTotal: hideTotalWhenDisabled && nextStepDisabled ? NaN : oldTotal,
      currency: this.props.currency,
      customPaymentMode: this.props.customPaymentMode,
      hideGiftcardButton: this.props.hideGiftcardButton,
      mobilePositionBottom: this.props.mobilePositionBottom,
    }

    const memberGetMemberTrigger =
      this.props.member.name !== '' ? (
        <FormMessage
          type={FormMessageType.Info}
          show={this.props.member.show}
          onClose={this.props.hideTrigger}
        >
          <DisplayContent contentKey="member-get-member-trigger">
            Input your voucher code here
          </DisplayContent>
        </FormMessage>
      ) : (
        undefined
      )

    return (
      <div
        ref="ref"
        style={
          !this.props.mobilePositionBottom
            ? { position: 'relative' }
            : undefined
        }
      >
        <Footer
          footerButtonsLayoutClassName={
            this.props?.footerButtonsLayoutClassName || ''
          }
          giftCardLength={this.props.giftCards.length}
          onClickGiftCard={this.handleGiftcard}
          detailsOpen={this.props.viewSummary}
          onClickMoreDetails={() =>
            this.props.setSummary(!this.props.viewSummary)
          }
          giftcardNotification={memberGetMemberTrigger}
          // trigger={<TotalPriceTrigger stickyStop={this.state.reachedStop} />}
          nextStepLink={
            nextStepDisabled || nextStepOnClick ? (
              <NextStepButton {...this.props} />
            ) : (
              <Slink name={nextStepLink} params={nextStepLinkParams}>
                <NextStepButton {...this.props} />
              </Slink>
            )
          }
          stickyStop={this.state.reachedStop}
          {...passedProps}
        />
        <Overlay
          open={this.props.viewSummary}
          onClose={() => this.props.setSummary(false)}
          stickyStop={this.state.reachedStop}
          label={
            <DisplayContent contentKey="close-details">
              Close details
            </DisplayContent>
          }
        >
          <Summary hideGiftcardButton={hideGiftcardButton} />
        </Overlay>
      </div>
    )
  }

  private handleGiftcard = () => {
    this.props.hideTrigger()
    this.props.openGiftcardModal()
  }

  private isElementInViewport = (div: HTMLDivElement) => {
    if (div !== undefined) {
      const rect = div.getBoundingClientRect()

      return (
        rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight)
      )
    }
  }

  private handler = () => {
    const div = this.refs.ref as HTMLDivElement
    if (this.isElementInViewport(div) && !this.state.reachedStop) {
      this.setState({ reachedStop: true })
    }
    if (!this.isElementInViewport(div) && this.state.reachedStop) {
      this.setState({ reachedStop: false })
    }
  }
}

const FunnelFooter = connect(
  (state: AppState, ownProps: FunnelFooterOwnProps) => ({
    currency: ownProps.customPaymentMode
      ? state.paymentOrder.customPayment
        ? state.paymentOrder.customPayment.Currency
        : SiteCurrency.euro
      : state.locale.currency,
    total: ownProps.customPaymentMode
      ? state.paymentOrder.customPayment
        ? state.paymentOrder.customPayment.Total
        : NaN
      : state.funnelOrder.total,
    oldTotal: state.funnelOrder.originalTotal,
    giftCards: state.funnelOrder.giftcards,
    viewSummary: state.ui.viewSummary,
    member: state.memberGetMember,
  }),
  dispatch => ({
    openGiftcardModal: () => {
      dispatch(actions.change('ui.viewGiftcardModal', true))
    },
    setSummary: (val: boolean) => {
      dispatch(actions.change('ui.viewSummary', val))
    },
    hideTrigger: () => {
      dispatch(hideMember())
    },
  }),
)(Funnel)

export default FunnelFooter
